import { useTranslations } from 'next-intl'
import { useCallback } from 'react'

import emDash from '~/components/emdash'
import type { FuelTankFuelType, FuelType, TankShape } from '~/data/api/client/ders'

import type { FuelUnit } from '../types/units'
import { useNumberFormatter } from './number'
import type { FormatterHook } from './types'
import { combineOptions } from './types'
import type { UnitsFormat } from './units'
import { formatUnitDesc, formatUnits, formatUnitsAbbr } from './units'

const fuelFormats: UnitsFormat<FuelUnit> = {
  gal: {
    long: 'Gallons',
    short: 'gal'
  },
  igal: {
    long: 'Gallons',
    short: 'gal'
  },
  L: {
    long: 'Liters',
    short: 'L'
  }
}

function isFuelUnit(unit?: string): unit is FuelUnit {
  return unit !== undefined && unit in fuelFormats
}

export const formatFuelUnits = formatUnits(fuelFormats)
export const formatFuelUnitsAbbr = formatUnitsAbbr(fuelFormats)
export const formatFuelUnitsDesc = formatUnitDesc(fuelFormats)

const defaultOptions = {
  longForm: false,
  sigFigs: 1,
  units: 'gal',
  omitUnits: false,
  invalidStr: emDash
}

const exportOptions = { ...defaultOptions, omitUnits: true, invalidStr: '' }

export const useFuelFormatter: FormatterHook = () => {
  const formatNumber = useNumberFormatter()

  return useCallback(
    (value, options) => {
      const {
        value: fuel,
        longForm,
        sigFigs,
        units,
        omitUnits,
        invalidStr
      } = combineOptions({
        value,
        options,
        defaults: defaultOptions,
        exports: exportOptions
      })

      if (!isFuelUnit(units)) return invalidStr
      if (typeof fuel !== 'string' && typeof fuel !== 'number') return invalidStr

      // Coerce to number
      const fuelNum = parseFloat(`${fuel}`)
      if (isNaN(fuelNum)) return invalidStr

      const fuelStr = formatNumber(fuelNum, { maximumFractionDigits: sigFigs, export: options?.export })
      if (omitUnits) return fuelStr

      const formattedUnits = longForm ? formatFuelUnits(units) : formatFuelUnitsAbbr(units)
      return [fuelStr, formattedUnits].join(' ')
    },
    [formatNumber]
  )
}

/**
 * Hook that returns a formatter for a fuel tank's fuel type.
 *
 * Reads the `settings.fuelTanks` namespace rather than a `data.*` one: the tank
 * form already labels every enum member there, and every fuel tank surface
 * should show the same words. The template literal keeps the key a literal
 * union, so TS still verifies each member against the locale file.
 */
export function useFuelTypeFormatter() {
  const t = useTranslations('settings.fuelTanks')
  return useCallback((fuelType: FuelTankFuelType): string => t(`FuelTankDetailsForm_fuelTypes_${fuelType}`), [t])
}

/**
 * Hook that returns a formatter for a generator's fuel type.
 *
 * A generator burns a different set of fuels from the one a tank stores (LP vapor
 * rather than propane), so it has its own vocabulary, labelled by the generator
 * form in `settings.generators`; every generator surface should show those words.
 */
export function useGeneratorFuelTypeFormatter() {
  const t = useTranslations('settings.generators')
  return useCallback((fuelType: FuelType): string => t(`GeneratorDetailsForm_fuelTypes_${fuelType}`), [t])
}

/** Hook that returns a formatter for a fuel tank's vessel geometry. */
export function useTankShapeFormatter() {
  const t = useTranslations('settings.fuelTanks')
  return useCallback((tankShape: TankShape): string => t(`FuelTankDetailsForm_tankShapes_${tankShape}`), [t])
}
